import React, { useEffect, useMemo, useState } from 'react';
import { GripVertical, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';
import { ProductCardCustomization } from './ProductCardCustomization';

interface TenantPagesCustomizationProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

interface SubjectOption {
  id: string;
  label: string;
  value: string;
  textColor?: string;
  backgroundColor?: string;
}

type AdminFAQItem = { id: string; category: string; question: string; answer: string };

type ContactHeaderKey = 'badgeLabel' | 'title' | 'line1' | 'line2';
type ShopHeaderKey = 'badgeLabel' | 'title' | 'line1' | 'line2';
type BlogHeaderKey = 'badgeLabel' | 'title' | 'line1' | 'line2';
type ElearningHeaderKey = 'badgeLabel' | 'title' | 'line1' | 'line2';

// ✅ SUPPRIMÉ : Plus de valeurs par défaut hardcodées
// Les utilisateurs doivent configurer manuellement leurs options et FAQ

export const TenantPagesCustomization: React.FC<TenantPagesCustomizationProps> = ({ formData, handleConfigChange }) => {
  const { tenant, setPreviewValue } = useTenantContext();
  const [activePageTab, setActivePageTab] = useState<'contact' | 'shop' | 'blog' | 'faq' | 'elearning'>('contact');

  const themeDefaults = {
    text: (formData?.config?.color3 as string) || '#ffffff',
    bg: '#111827',
  };

  // ✅ SUPPRIMÉ : Plus de valeurs par défaut
  // Tous les champs doivent être configurés manuellement par l'utilisateur

  const contactHeaderConfig = (formData?.config?.pages?.contact?.header ?? {}) as Partial<Record<ContactHeaderKey, string>>;
  const shopHeaderConfig = (formData?.config?.pages?.shop?.header ?? {}) as Partial<Record<ShopHeaderKey, string>>;
  const blogHeaderConfig = (formData?.config?.pages?.blog?.header ?? {}) as Partial<Record<BlogHeaderKey, string>>;
  const elearningHeaderConfig = (formData?.config?.pages?.elearning?.header ?? {}) as Partial<Record<ElearningHeaderKey, string>>;

  // ✅ AUCUNE VALEUR PAR DÉFAUT - Afficher les champs vides si rien dans la BD
  const contactHeader = React.useMemo(() => ({
    badgeLabel: contactHeaderConfig?.badgeLabel || '',
    title: contactHeaderConfig?.title || '',
    line1: contactHeaderConfig?.line1 || '',
    line2: contactHeaderConfig?.line2 || '',
  }), [contactHeaderConfig?.badgeLabel, contactHeaderConfig?.line1, contactHeaderConfig?.line2, contactHeaderConfig?.title]);

  const shopHeader = React.useMemo(() => ({
    badgeLabel: shopHeaderConfig?.badgeLabel || '',
    title: shopHeaderConfig?.title || '',
    line1: shopHeaderConfig?.line1 || '',
    line2: shopHeaderConfig?.line2 || '',
  }), [shopHeaderConfig?.badgeLabel, shopHeaderConfig?.line1, shopHeaderConfig?.line2, shopHeaderConfig?.title]);

  const blogHeader = React.useMemo(() => ({
    badgeLabel: blogHeaderConfig?.badgeLabel || '',
    title: blogHeaderConfig?.title || '',
    line1: blogHeaderConfig?.line1 || '',
    line2: blogHeaderConfig?.line2 || '',
  }), [blogHeaderConfig?.badgeLabel, blogHeaderConfig?.line1, blogHeaderConfig?.line2, blogHeaderConfig?.title]);

  const elearningHeader = React.useMemo(() => ({
    badgeLabel: elearningHeaderConfig?.badgeLabel || '',
    title: elearningHeaderConfig?.title || '',
    line1: elearningHeaderConfig?.line1 || '',
    line2: elearningHeaderConfig?.line2 || '',
  }), [elearningHeaderConfig?.badgeLabel, elearningHeaderConfig?.line1, elearningHeaderConfig?.line2, elearningHeaderConfig?.title]);

  const handleContactHeaderChange = (key: ContactHeaderKey, value: string) => {
    handleConfigChange(`pages.contact.header.${key}`, value);
    setPreviewValue(`pages.contact.header.${key}`, value);
  };

  const handleShopHeaderChange = (key: ShopHeaderKey, value: string) => {
    handleConfigChange(`pages.shop.header.${key}`, value);
    setPreviewValue(`pages.shop.header.${key}`, value);
  };

  // ✅ SUPPRIMÉ : Pas de réinitialisation avec valeurs par défaut

  const handleBlogHeaderChange = (key: BlogHeaderKey, value: string) => {
    handleConfigChange(`pages.blog.header.${key}`, value);
    setPreviewValue(`pages.blog.header.${key}`, value);
  };

  const handleElearningHeaderChange = (key: ElearningHeaderKey, value: string) => {
    handleConfigChange(`pages.elearning.header.${key}`, value);
    setPreviewValue(`pages.elearning.header.${key}`, value);
  };

  // ✅ SUPPRIMÉ : Pas de réinitialisation avec valeurs par défaut

  // ✅ AUCUNE VALEUR PAR DÉFAUT - Retourner tableau vide si rien dans la BD
  const subjectOptions: SubjectOption[] = useMemo(() => {
    const options = formData?.config?.pages?.contact?.subjectOptions as SubjectOption[] | undefined;
    if (options && Array.isArray(options) && options.length > 0) return options.map((o, idx) => ({
      id: o.id || `opt_${idx}`,
      label: o.label ?? o.value ?? '',
      value: o.value ?? o.label ?? '',
      textColor: o.textColor || themeDefaults.text,
      backgroundColor: o.backgroundColor || themeDefaults.bg,
    }));
    // ✅ Retourner tableau vide au lieu des valeurs par défaut
    return [];
  }, [formData?.config?.pages?.contact?.subjectOptions, formData?.config?.color3]);

  // ⚠️ DÉSACTIVÉ : Ce useEffect écrasait constamment les configurations
  // Les valeurs par défaut sont maintenant gérées uniquement via les useMemo ci-dessus
  // Si vous avez besoin d'initialiser un nouveau tenant, faites-le manuellement dans l'admin
  
  // ANCIEN CODE PROBLÉMATIQUE COMMENTÉ :
  // useEffect(() => {
  //   // Ce code s'exécutait à chaque render et écrasait les configs existantes
  //   // Contact header - seulement si vraiment absent
  //   const headerConfig = formData?.config?.pages?.contact?.header;
  //   ...
  // }, [formData, tenant, contactHeaderDefaults, shopHeaderDefaults, themeDefaults]);
  
  // ✅ NOUVELLE APPROCHE : Les valeurs par défaut sont appliquées UNIQUEMENT dans l'affichage
  // via les useMemo (contactHeader, shopHeader, blogHeader, etc.)
  // Les données dans formData ne sont JAMAIS écrasées automatiquement

  const persist = (next: SubjectOption[]) => {
    handleConfigChange('pages.contact.subjectOptions', next);
    setPreviewValue('pages.contact.subjectOptions', next);
  };

  const addOption = () => {
    const base: SubjectOption = {
      id: `opt_${Date.now()}`,
      label: 'Nouvelle option',
      value: 'Nouvelle option',
      textColor: themeDefaults.text,
      backgroundColor: themeDefaults.bg,
    };
    persist([...(subjectOptions || []), base]);
  };

  const deleteOption = (id: string) => {
    const next = subjectOptions.filter(o => o.id !== id);
    persist(next);
  };

  const moveOption = (id: string, dir: -1 | 1) => {
    const idx = subjectOptions.findIndex(o => o.id === id);
    if (idx === -1) return;
    const ni = idx + dir;
    if (ni < 0 || ni >= subjectOptions.length) return;
    const next = [...subjectOptions];
    const [item] = next.splice(idx, 1);
    next.splice(ni, 0, item);
    persist(next);
  };

  const updateOption = (id: string, patch: Partial<SubjectOption>) => {
    const next = subjectOptions.map(o => (o.id === id ? { ...o, ...patch } : o));
    persist(next);
  };

  // ✅ SUPPRIMÉ : Pas de réinitialisation avec valeurs par défaut

  return (
    <section className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Pages — Personnalisation</h3>
      </div>

      {/* Onglets des pages */}
      <div className="flex space-x-1 bg-white/5 p-1 rounded-lg border border-white/10 overflow-x-auto">
        <button
          onClick={() => setActivePageTab('contact')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activePageTab === 'contact'
              ? 'bg-white/10 text-white border border-white/20'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Page Contact
        </button>
        <button
          onClick={() => setActivePageTab('shop')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activePageTab === 'shop'
              ? 'bg-white/10 text-white border border-white/20'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Page Boutique
        </button>
        <button
          onClick={() => setActivePageTab('blog')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activePageTab === 'blog'
              ? 'bg-white/10 text-white border border-white/20'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Page Blog
        </button>
        <button
          onClick={() => setActivePageTab('elearning')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activePageTab === 'elearning'
              ? 'bg-white/10 text-white border border-white/20'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Page E-Learning
        </button>
        <button
          onClick={() => setActivePageTab('faq')}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
            activePageTab === 'faq'
              ? 'bg-white/10 text-white border border-white/20'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Page FAQ
        </button>
      </div>

      {/* Contenu de l'onglet Contact */}
      {activePageTab === 'contact' && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-white">Page Contact — "Objet de votre demande"</h4>
            <div className="space-x-2">
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-md text-sm border border-white/10"
              >
                <Plus className="h-4 w-4 mr-1" /> Ajouter une option
              </button>
              {/* ✅ SUPPRIMÉ : Bouton Réinitialiser */}
            </div>
          </div>
          <p className="text-sm text-gray-400 mb-6">Ajoutez, modifiez, supprimez et organisez les options visibles dans la liste. Vous pouvez également définir les couleurs de texte et de fond de chaque option.</p>

          <div className="bg-black/20 border border-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-white font-medium">En-tête de la page Contact</h5>
                <p className="text-xs text-gray-400">Personnalisez les quatre lignes principales visibles sur la page publique.</p>
              </div>
              {/* ✅ SUPPRIMÉ : Bouton Réinitialiser */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Badge supérieur</label>
                <input
                  type="text"
                  value={contactHeader.badgeLabel}
                  onChange={(e) => handleContactHeaderChange('badgeLabel', e.target.value)}
                  placeholder="Ex: Entrer en contact"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Titre principal</label>
                <input
                  type="text"
                  value={contactHeader.title}
                  onChange={(e) => handleContactHeaderChange('title', e.target.value)}
                  placeholder="Ex: Contacter notre équipe"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 1</label>
                <input
                  type="text"
                  value={contactHeader.line1}
                  onChange={(e) => handleContactHeaderChange('line1', e.target.value)}
                  placeholder="Une question, une demande d'information..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 2</label>
                <input
                  type="text"
                  value={contactHeader.line2}
                  onChange={(e) => handleContactHeaderChange('line2', e.target.value)}
                  placeholder="Notre équipe est là pour vous accompagner"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          </div>

        <div className="space-y-4">
          {subjectOptions.map((opt, idx) => (
            <div key={opt.id} className="flex flex-col md:flex-row md:items-center md:space-x-4 bg-black/20 border border-white/10 rounded-lg p-4">
              <div className="flex items-center mb-3 md:mb-0">
                <GripVertical className="h-5 w-5 text-gray-500 mr-3" />
                <span className="text-sm text-gray-400 w-12">#{idx + 1}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 flex-1">
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Libellé</label>
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => updateOption(opt.id, { label: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs text-gray-400 mb-1">Valeur</label>
                  <input
                    type="text"
                    value={opt.value}
                    onChange={(e) => updateOption(opt.id, { value: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Couleur du texte</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={opt.textColor || themeDefaults.text}
                      onChange={(e) => updateOption(opt.id, { textColor: e.target.value })}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={opt.textColor || themeDefaults.text}
                      onChange={(e) => updateOption(opt.id, { textColor: e.target.value })}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Couleur du fond</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={opt.backgroundColor || themeDefaults.bg}
                      onChange={(e) => updateOption(opt.id, { backgroundColor: e.target.value })}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={opt.backgroundColor || themeDefaults.bg}
                      onChange={(e) => updateOption(opt.id, { backgroundColor: e.target.value })}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center mt-3 md:mt-0 space-x-2">
                <button
                  type="button"
                  onClick={() => moveOption(opt.id, -1)}
                  className="p-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-md border border-white/10"
                  title="Monter"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveOption(opt.id, 1)}
                  className="p-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-md border border-white/10"
                  title="Descendre"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteOption(opt.id)}
                  className="p-2 bg-red-600/80 hover:bg-red-600 text-white rounded-md border border-red-800"
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

          
        </div>
      )}

      {/* Contenu de l'onglet Boutique */}
      {activePageTab === 'shop' && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-white">Page Boutique — En-tête</h4>
          </div>
          <p className="text-sm text-gray-400 mb-6">Personnalisez les quatre lignes principales visibles sur la page boutique publique.</p>

          <div className="bg-black/20 border border-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-white font-medium">En-tête de la page Boutique</h5>
                <p className="text-xs text-gray-400">Personnalisez les quatre lignes principales visibles sur la page publique.</p>
              </div>
              {/* ✅ SUPPRIMÉ : Bouton Réinitialiser */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Badge supérieur</label>
                <input
                  type="text"
                  value={shopHeader.badgeLabel}
                  onChange={(e) => handleShopHeaderChange('badgeLabel', e.target.value)}
                  placeholder="Ex: Boutique SIS"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Titre principal</label>
                <input
                  type="text"
                  value={shopHeader.title}
                  onChange={(e) => handleShopHeaderChange('title', e.target.value)}
                  placeholder="Ex: Produits SIS"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 1</label>
                <input
                  type="text"
                  value={shopHeader.line1}
                  onChange={(e) => handleShopHeaderChange('line1', e.target.value)}
                  placeholder="Découvrez notre sélection de produits..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 2</label>
                <input
                  type="text"
                  value={shopHeader.line2}
                  onChange={(e) => handleShopHeaderChange('line2', e.target.value)}
                  placeholder="Trouvez la solution adaptée à vos besoins"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Personnalisation ProductCard */}
          <div className="bg-black/20 border border-white/10 rounded-lg p-4">
            <div className="mb-4">
              <h5 className="text-white font-medium">🎴 Personnalisation des cartes produits</h5>
              <p className="text-xs text-gray-400">Personnalisez l'apparence, la disposition et les effets des cartes produits de la boutique.</p>
            </div>
            <ProductCardCustomization 
              formData={formData} 
              handleConfigChange={handleConfigChange}
              setPreviewValue={setPreviewValue}
            />
          </div>
        </div>
      )}

      {/* Contenu de l'onglet Blog */}
      {activePageTab === 'blog' && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-white">Page Blog — En-tête</h4>
          </div>
          <p className="text-sm text-gray-400 mb-6">Personnalisez les quatre lignes principales visibles sur la page blog publique.</p>

          <div className="bg-black/20 border border-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-white font-medium">En-tête de la page Blog</h5>
                <p className="text-xs text-gray-400">Personnalisez les quatre lignes principales visibles sur la page publique.</p>
              </div>
              {/* ✅ SUPPRIMÉ : Bouton Réinitialiser */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Badge supérieur</label>
                <input
                  type="text"
                  value={blogHeader.badgeLabel}
                  onChange={(e) => handleBlogHeaderChange('badgeLabel', e.target.value)}
                  placeholder="Ex: Blog ECATHEBA"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Titre principal</label>
                <input
                  type="text"
                  value={blogHeader.title}
                  onChange={(e) => handleBlogHeaderChange('title', e.target.value)}
                  placeholder="Ex: Réflexions théologiques"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 1</label>
                <input
                  type="text"
                  value={blogHeader.line1}
                  onChange={(e) => handleBlogHeaderChange('line1', e.target.value)}
                  placeholder="Découvrez nos articles, réflexions spirituelles..."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 2</label>
                <input
                  type="text"
                  value={blogHeader.line2}
                  onChange={(e) => handleBlogHeaderChange('line2', e.target.value)}
                  placeholder="Approfondissez votre foi et votre compréhension"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenu de l'onglet E-Learning */}
      {activePageTab === 'elearning' && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-white">Page E-Learning — En-tête</h4>
          </div>
          <p className="text-sm text-gray-400 mb-6">Personnalisez les quatre lignes principales visibles sur la page des formations e-learning publique.</p>

          <div className="bg-black/20 border border-white/10 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="text-white font-medium">En-tête de la page E-Learning</h5>
                <p className="text-xs text-gray-400">Personnalisez les quatre lignes principales visibles sur la page publique.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Badge supérieur</label>
                <input
                  type="text"
                  value={elearningHeader.badgeLabel}
                  onChange={(e) => handleElearningHeaderChange('badgeLabel', e.target.value)}
                  placeholder="Ex: Formations d'excellence"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Titre principal</label>
                <input
                  type="text"
                  value={elearningHeader.title}
                  onChange={(e) => handleElearningHeaderChange('title', e.target.value)}
                  placeholder="Ex: Nos formations théologiques"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 1</label>
                <input
                  type="text"
                  value={elearningHeader.line1}
                  onChange={(e) => handleElearningHeaderChange('line1', e.target.value)}
                  placeholder="Ex: Approfondissez votre foi avec nos formations théologiques d'excellence."
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Sous-titre — ligne 2</label>
                <input
                  type="text"
                  value={elearningHeader.line2}
                  onChange={(e) => handleElearningHeaderChange('line2', e.target.value)}
                  placeholder="Ex: Transformez votre compréhension spirituelle"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenu de l'onglet FAQ */}
      {activePageTab === 'faq' && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <h4 className="text-base font-semibold text-white mb-4">Page FAQ — Questions/Réponses</h4>
        <p className="text-sm text-gray-400 mb-6">Gérez les questions/réponses, leur catégorie et l'ordre d'affichage. Vous pouvez aussi personnaliser le titre, le sous-titre et le placeholder de recherche.</p>

        {/* En-tête FAQ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Titre</label>
            <input
              type="text"
              value={formData.config.pages?.faq?.headerTitle ?? 'Questions Fréquentes'}
              onChange={(e) => {
                const val = e.target.value;
                handleConfigChange('pages.faq.headerTitle', val);
                setPreviewValue('pages.faq.headerTitle', val);
              }}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="Questions Fréquentes"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Sous-titre</label>
            <input
              type="text"
              value={formData.config.pages?.faq?.headerSubtitle ?? `Trouvez rapidement les réponses à vos questions sur ${tenant?.name || 'notre site'}`}
              onChange={(e) => {
                const val = e.target.value;
                handleConfigChange('pages.faq.headerSubtitle', val);
                setPreviewValue('pages.faq.headerSubtitle', val);
              }}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="Trouvez rapidement les réponses à vos questions"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Placeholder de recherche</label>
            <input
              type="text"
              value={formData.config.pages?.faq?.searchPlaceholder ?? 'Rechercher une question...'}
              onChange={(e) => {
                const val = e.target.value;
                handleConfigChange('pages.faq.searchPlaceholder', val);
                setPreviewValue('pages.faq.searchPlaceholder', val);
              }}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              placeholder="Rechercher une question..."
            />
          </div>
        </div>

        {/* Items FAQ */}
        {(() => {
          // ✅ AUCUNE VALEUR PAR DÉFAUT - Retourner tableau vide si rien dans la BD
          const faqItems: AdminFAQItem[] = (formData.config.pages?.faq?.items as AdminFAQItem[] | undefined) || [];

          const persistFaq = (next: AdminFAQItem[]) => {
            handleConfigChange('pages.faq.items', next);
            setPreviewValue('pages.faq.items', next);
          };

          const addFaq = () => {
            const base: AdminFAQItem = { id: `faq_${Date.now()}`, category: 'Général', question: 'Nouvelle question', answer: 'Nouvelle réponse' };
            persistFaq([...(faqItems || []), base]);
          };

          const deleteFaq = (id: string) => {
            const next = faqItems.filter((f) => f.id !== id);
            persistFaq(next);
          };

          const moveFaq = (id: string, dir: -1 | 1) => {
            const idx = faqItems.findIndex((f) => f.id === id);
            if (idx === -1) return;
            const ni = idx + dir;
            if (ni < 0 || ni >= faqItems.length) return;
            const next = [...faqItems];
            const [item] = next.splice(idx, 1);
            next.splice(ni, 0, item);
            persistFaq(next);
          };

          const updateFaq = (id: string, patch: Partial<AdminFAQItem>) => {
            const next = faqItems.map((f) => (f.id === id ? { ...f, ...patch } : f));
            persistFaq(next);
          };

          return (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h5 className="text-white font-medium">Questions ({faqItems.length})</h5>
                <button type="button" onClick={addFaq} className="inline-flex items-center px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-md text-sm border border-white/10">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter une question
                </button>
              </div>

              {faqItems.length === 0 ? (
                <div className="text-sm text-gray-400">Aucune question pour le moment.</div>
              ) : (
                <div className="space-y-4">
                  {faqItems.map((faq, idx) => (
                    <div key={faq.id} className="bg-black/20 border border-white/10 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <GripVertical className="h-5 w-5 text-gray-500 mr-3" />
                          <span className="text-sm text-gray-400 w-12">#{idx + 1}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button type="button" onClick={() => moveFaq(faq.id, -1)} className="p-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-md border border-white/10" title="Monter">
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => moveFaq(faq.id, 1)} className="p-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-md border border-white/10" title="Descendre">
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => deleteFaq(faq.id)} className="p-2 bg-red-600/80 hover:bg-red-600 text-white rounded-md border border-red-800" title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Catégorie</label>
                          <input
                            type="text"
                            value={faq.category}
                            onChange={(e) => updateFaq(faq.id, { category: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs text-gray-400 mb-1">Question</label>
                          <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => updateFaq(faq.id, { question: e.target.value })}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-xs text-gray-400 mb-1">Réponse</label>
                          <textarea
                            value={faq.answer}
                            onChange={(e) => updateFaq(faq.id, { answer: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        </div>
      )}
    </section>
  );
};
